import React, { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { localizeApiError } from "@/i18n/apiErrors";
import { useTutorial } from "@/contexts/TutorialContext";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Target, UtensilsCrossed, Users, Wallet, Leaf, Heart, Check, CheckCheck } from "lucide-react";

const GOALS = [
  "goal_organized", "goal_diet", "goal_lose_weight", "goal_gain_weight",
  "goal_save_money", "goal_track_habits", "goal_save_time", "goal_recipe_ideas",
  "goal_health", "goal_family", "goal_muscle", "goal_pregnancy", "goal_other",
];

const DIET_TYPES = [
  { key: "omnivore", desc: null },
  { key: "pesco_pollo", desc: "diet_pesco_pollo_desc" },
  { key: "pescatarian", desc: "diet_pescatarian_desc" },
  { key: "vegetarian", desc: null },
  { key: "vegan", desc: null },
];

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];
const WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const CURRENCIES = ["$", "€", "₴", "£"];
const RESTRICTIONS = ["vegetarian", "vegan", "glutenFree", "lactoseFree", "nutFree", "halal"];
const SKILLS = ["beginner", "intermediate", "advanced"];
const TIMES = ["quick", "medium", "long"];

// Categorized favorite ingredients matching Fridge structure
const FAV_CATEGORIES = {
  meat: {
    icon: "🥩",
    items: ["fav_chicken", "fav_beef", "fav_pork", "fav_turkey", "fav_lamb", "fav_bacon", "fav_sausage"]
  },
  seafood: {
    icon: "🐟",
    items: ["fav_fish", "fav_salmon", "fav_shrimp", "fav_tuna", "fav_cod", "fav_mussels"]
  },
  dairy: {
    icon: "🥛",
    items: ["fav_milk", "fav_cheese", "fav_yogurt", "fav_butter", "fav_cream", "fav_cottage_cheese", "fav_eggs"]
  },
  vegetables: {
    icon: "🥕",
    items: ["fav_tomato", "fav_potato", "fav_onion", "fav_garlic", "fav_carrot", "fav_pepper", "fav_cucumber", "fav_broccoli", "fav_spinach", "fav_mushrooms", "fav_zucchini", "fav_cabbage", "fav_eggplant"]
  },
  fruits: {
    icon: "🍎",
    items: ["fav_banana", "fav_apple", "fav_orange", "fav_lemon", "fav_avocado", "fav_berries", "fav_grapes"]
  },
  grains: {
    icon: "🌾",
    items: ["fav_rice", "fav_pasta", "fav_bread", "fav_oats", "fav_buckwheat", "fav_quinoa", "fav_flour"]
  },
  legumes: {
    icon: "🫘",
    items: ["fav_beans", "fav_lentils", "fav_chickpeas", "fav_peas", "fav_tofu"]
  },
  bakery: {
    icon: "🥖",
    items: ["fav_lavash", "fav_bun", "fav_pastry", "fav_gluten_free_bread", "fav_crackers", "fav_pita"]
  },
};

// Extended translations for new ingredients

export default function OnboardingPage({ isRedoing, onComplete }) {
  const { api, updateUser } = useAuth();
  const { t, lang } = useLanguage();
  const { startTutorial } = useTutorial();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otherGoal, setOtherGoal] = useState("");
  const [showConsent, setShowConsent] = useState(false);
  const [consentGranted, setConsentGranted] = useState(true);  // default ON
  const payloadRef = useRef(null);
  const [data, setData] = useState({
    goals: [],
    diet_type: "omnivore",
    meal_types: ["breakfast", "lunch", "dinner"],
    family_size: 2,
    monthly_budget: "",
    budget_currency: "$",
    first_day_of_week: "monday",
    dietary_restrictions: [],
    cooking_skill: "intermediate",
    cooking_time_preference: "medium",
    favorite_ingredients: [],
  });


  const totalSteps = 6;
  const update = (key, value) => setData((prev) => ({ ...prev, [key]: value }));
  const toggleArray = (key, value) => {
    setData((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value],
    }));
  };

  // Bulk select all items in a category
  const selectAllInCategory = (catKey) => {
    const items = FAV_CATEGORIES[catKey].items;
    const allSelected = items.every((item) => data.favorite_ingredients.includes(item));
    
    if (allSelected) {
      // Deselect all in category
      setData((prev) => ({
        ...prev,
        favorite_ingredients: prev.favorite_ingredients.filter((ing) => !items.includes(ing)),
      }));
    } else {
      // Select all in category
      setData((prev) => ({
        ...prev,
        favorite_ingredients: [...new Set([...prev.favorite_ingredients, ...items])],
      }));
    }
  };

  const isCategoryFullySelected = (catKey) => {
    const items = FAV_CATEGORIES[catKey].items;
    return items.every((item) => data.favorite_ingredients.includes(item));
  };

  const goNext = () => { if (step < totalSteps) setStep(step + 1); else handleFinish(); };
  const goBack = () => { if (step > 1) setStep(step - 1); };

  const handleFinish = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = { ...data };
      if (otherGoal && data.goals.includes("goal_other")) {
        payload.goals = [...payload.goals.filter(g => g !== "goal_other"), `other:${otherGoal}`];
      }
      await api().post("/onboarding", payload);
      if (isRedoing && onComplete) {
        onComplete();
      } else {
        // Onboarding saved. Stash the payload and show the analytics-consent
        // screen; finishConsent starts the tutorial and navigates onward.
        payloadRef.current = payload;
        setShowConsent(true);
      }
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 429) {
        setError(t("errRateLimit"));
      } else {
        setError(localizeApiError(err?.response?.data?.detail, t));
      }
    }
    setLoading(false);
  };

  const finishConsent = async () => {
    setLoading(true);
    try {
      await api().post("/consent/analytics", { granted: consentGranted });
    } catch (err) {
      console.error(err);   // consent is not critical for proceeding
    }
    // Start tutorial BEFORE updating user state (which triggers re-render)
    startTutorial();
    localStorage.setItem("mealpill_onboarding_completed", "true");
    updateUser({
      onboarding_completed: true,
      preferences: payloadRef.current,
      consent_analytics: consentGranted,
    });
    setTimeout(() => navigate("/planner"), 100);
  };

  const PillBtn = ({ active, onClick, children, testId }) => (
    <button
      data-testid={testId}
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={`px-4 py-2.5 rounded-full border-2 transition-all font-body text-sm font-semibold ${
        active ? "border-[#7B9E7F] bg-[#7B9E7F] text-white" : "border-[#E8E5E0] bg-white text-[#6B705C] hover:border-[#A5A58D]"
      }`}
    >
      {children}
    </button>
  );

  const CardBtn = ({ active, onClick, children, testId, subtitle }) => (
    <button
      data-testid={testId}
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={`w-full p-4 rounded-2xl border-2 transition-all font-body text-left relative ${
        active ? "border-[#7B9E7F] bg-[#7B9E7F]/8" : "border-[#E8E5E0] bg-white hover:border-[#A5A58D]"
      }`}
    >
      {active && <Check className="absolute top-3 right-3 w-5 h-5 text-[#7B9E7F]" strokeWidth={2} />}
      <span className={`font-semibold ${active ? "text-[#4A6B4E]" : "text-[#3D4035]"}`}>{children}</span>
      {subtitle && <p className="text-xs text-[#A5A58D] mt-0.5">{subtitle}</p>}
    </button>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-3" data-testid="onboarding-step-1">
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-2xl text-[#3D4035]">{t("goalTitle")}</h2>
                <p className="font-body text-sm text-[#A5A58D]">{t("goalSubtitle")}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {GOALS.map((goal) => (
                <PillBtn key={goal} testId={`goal-${goal}`} active={data.goals.includes(goal)} onClick={() => toggleArray("goals", goal)}>
                  {t(goal)}
                </PillBtn>
              ))}
            </div>
            {data.goals.includes("goal_other") && (
              <input
                data-testid="goal-other-input"
                type="text"
                value={otherGoal}
                onChange={(e) => setOtherGoal(e.target.value)}
                placeholder="..."
                className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none mt-2"
              />
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-3" data-testid="onboarding-step-2">
            <div className="flex items-center gap-3 mb-4">
              <Leaf className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />
              <h2 className="font-heading text-2xl text-[#3D4035]">{t("dietTitle")}</h2>
            </div>
            <div className="space-y-2">
              {DIET_TYPES.map(({ key, desc }) => (
                <CardBtn key={key} testId={`diet-${key}`} active={data.diet_type === key} onClick={() => update("diet_type", key)} subtitle={desc ? t(desc) : null}>
                  {t(`diet_${key}`)}
                </CardBtn>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6" data-testid="onboarding-step-3">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <UtensilsCrossed className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />
                <div>
                  <h2 className="font-heading text-2xl text-[#3D4035]">{t("mealsTitle")}</h2>
                  <p className="font-body text-sm text-[#A5A58D]">{t("mealsSubtitle")}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {MEAL_TYPES.map((meal) => (
                  <PillBtn key={meal} testId={`meal-type-${meal}`} active={data.meal_types.includes(meal)} onClick={() => toggleArray("meal_types", meal)}>
                    {t(meal)}
                  </PillBtn>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />
                <h2 className="font-heading text-xl text-[#3D4035]">{t("familyTitle")}</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`family-size-${n}`}
                    onClick={(e) => { e.preventDefault(); update("family_size", n); }}
                    className={`w-14 h-14 rounded-full font-body font-bold text-lg transition-all ${
                      data.family_size === n ? "bg-[#7B9E7F] text-white shadow-md" : "bg-white text-[#6B705C] border-2 border-[#E8E5E0]"
                    }`}>{n}</button>
                ))}
                <button type="button" data-testid="family-size-more"
                  onClick={(e) => { e.preventDefault(); update("family_size", 6); }}
                  className={`h-14 px-4 rounded-full font-body font-bold text-sm transition-all ${
                    data.family_size > 5 ? "bg-[#7B9E7F] text-white shadow-md" : "bg-white text-[#6B705C] border-2 border-[#E8E5E0]"
                  }`}>{t("moreThan5")}</button>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6" data-testid="onboarding-step-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Wallet className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl text-[#3D4035]">{t("budgetTitle")}</h2>
              </div>
              <p className="font-body text-xs text-[#7B9E7F] bg-[#7B9E7F]/10 rounded-xl px-4 py-2 mb-3">{t("budgetHint")}</p>
              
              {/* Currency selection with pill buttons instead of select */}
              <div className="flex gap-2 mb-3">
                {CURRENCIES.map((c) => (
                  <button key={c} type="button" data-testid={`currency-${c}`}
                    onClick={(e) => { e.preventDefault(); update("budget_currency", c); }}
                    className={`w-12 h-12 rounded-full font-body font-bold text-lg transition-all ${
                      data.budget_currency === c ? "bg-[#7B9E7F] text-white" : "bg-white text-[#6B705C] border-2 border-[#E8E5E0]"
                    }`}>{c}</button>
                ))}
              </div>
              
              <input
                data-testid="budget-amount-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={data.monthly_budget}
                onChange={(e) => update("monthly_budget", e.target.value.replace(/[^0-9]/g, ''))}
                placeholder={t("budgetPlaceholder")}
                className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-14 px-4 font-body text-[#3D4035] outline-none text-lg"
              />
            </div>
            <div>
              <p className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-3">{t("firstDayOfWeek")}</p>
              <div className="flex flex-wrap gap-2">
                {WEEK_DAYS.map((day) => (
                  <PillBtn key={day} testId={`first-day-${day}`} active={data.first_day_of_week === day} onClick={() => update("first_day_of_week", day)}>
                    {t(day)}
                  </PillBtn>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-5" data-testid="onboarding-step-5">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Leaf className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl text-[#3D4035]">{t("preferencesTitle")}</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {RESTRICTIONS.map((r) => (
                  <PillBtn key={r} testId={`restriction-${r}`} active={data.dietary_restrictions.includes(r)} onClick={() => toggleArray("dietary_restrictions", r)}>
                    {t(r)}
                  </PillBtn>
                ))}
              </div>
            </div>
            <div>
              <p className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2">{t("cookingSkill")}</p>
              <div className="flex gap-2">
                {SKILLS.map((skill) => (
                  <PillBtn key={skill} testId={`skill-${skill}`} active={data.cooking_skill === skill} onClick={() => update("cooking_skill", skill)}>
                    {t(skill)}
                  </PillBtn>
                ))}
              </div>
            </div>
            <div>
              <p className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2">{t("cookingTime")}</p>
              <div className="flex flex-wrap gap-2">
                {TIMES.map((time) => (
                  <PillBtn key={time} testId={`time-${time}`} active={data.cooking_time_preference === time} onClick={() => update("cooking_time_preference", time)}>
                    {t(time)}
                  </PillBtn>
                ))}
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4" data-testid="onboarding-step-6">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="w-6 h-6 text-[#7B9E7F]" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-2xl text-[#3D4035]">{t("favIngredientsTitle")}</h2>
                <p className="font-body text-sm text-[#A5A58D]">{t("favIngredientsSubtitle")}</p>
              </div>
            </div>
            
            {/* Categorized ingredients with bulk select */}
            <div className="space-y-4">
              {Object.entries(FAV_CATEGORIES).map(([catKey, { icon, items }]) => {
                const isFullySelected = isCategoryFullySelected(catKey);
                const selectedCount = items.filter((item) => data.favorite_ingredients.includes(item)).length;
                
                return (
                  <div key={catKey} className="bg-white rounded-2xl p-4 border border-black/5">
                    {/* Category Header with bulk select */}
                    <button
                      data-testid={`cat-select-${catKey}`}
                      onClick={() => selectAllInCategory(catKey)}
                      className="w-full flex items-center justify-between mb-3"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{icon}</span>
                        <span className="font-body font-bold text-[#3D4035]">{t(`cat_${catKey}`)}</span>
                        {selectedCount > 0 && (
                          <span className="text-xs bg-[#7B9E7F]/10 text-[#7B9E7F] px-2 py-0.5 rounded-full font-semibold">
                            {selectedCount}/{items.length}
                          </span>
                        )}
                      </div>
                      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
                        isFullySelected 
                          ? "bg-[#7B9E7F] text-white" 
                          : "bg-[#EBE9E4] text-[#6B705C] hover:bg-[#E0DDD7]"
                      }`}>
                        <CheckCheck className="w-4 h-4" strokeWidth={2} />
                        <span className="font-body text-xs font-semibold">
                          {isFullySelected ? t("deselectAll") : t("selectAllCategory")}
                        </span>
                      </div>
                    </button>
                    
                    {/* Individual items */}
                    <div className="flex flex-wrap gap-2">
                      {items.map((ing) => (
                        <PillBtn 
                          key={ing} 
                          testId={`fav-${ing}`} 
                          active={data.favorite_ingredients.includes(ing)} 
                          onClick={() => toggleArray("favorite_ingredients", ing)}
                        >
                          {t(ing)}
                        </PillBtn>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (showConsent) {
    return (
      <div className="fixed inset-0 bg-[#FAFAF8] flex flex-col z-[10002]" data-testid="consent-screen">
        <div className="flex-1 overflow-auto px-6 py-10 flex flex-col justify-center">
          <div className="w-full max-w-md mx-auto">
            <h1 className="font-heading text-3xl text-[#3D4035] mb-3">{t("consentTitle")}</h1>
            <p className="font-body text-[#6B705C] leading-relaxed mb-4">{t("consentBody")}</p>
            <p className="font-body text-sm text-[#A5A58D] mb-8">{t("consentNote")}</p>

            <div className="bg-white rounded-2xl p-5 border border-black/5">
              <div className="flex items-center justify-between">
                <span className="font-body font-semibold text-[#3D4035] pr-4">{t("consentToggle")}</span>
                <button
                  type="button"
                  data-testid="consent-toggle"
                  onClick={() => setConsentGranted((v) => !v)}
                  className={`relative shrink-0 w-14 h-8 rounded-full transition-colors ${consentGranted ? "bg-[#7B9E7F]" : "bg-[#E8E5E0]"}`}
                >
                  <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${consentGranted ? "translate-x-7" : "translate-x-1"}`} />
                </button>
              </div>
              <p className="font-body text-xs text-[#A5A58D] mt-3">{t("consentHint")}</p>
            </div>
          </div>
        </div>

        <div className="shrink-0 px-6 pt-3 bg-[#FAFAF8] border-t border-[#EBE9E4]/50" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}>
          <div className="w-full max-w-md mx-auto">
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="block text-center font-body text-sm text-[#7B9E7F] underline mb-3">
              {t("privacy")}
            </a>
            <button
              type="button"
              data-testid="consent-continue"
              onClick={finishConsent}
              disabled={loading}
              className="w-full bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold text-lg hover:bg-[#6B8E6F] transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t("consentContinue")
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#FAFAF8] flex flex-col z-[10002]" data-testid="onboarding-page">
      {/* Progress */}
      <div className="px-6 pt-6 pb-2 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <p className="font-body text-sm text-[#6B705C] font-semibold">
            {t("step")} {step} {t("of")} {totalSteps}
          </p>
          {!isRedoing && (
            <button type="button" data-testid="onboarding-skip" onClick={handleFinish} className="font-body text-sm text-[#A5A58D] hover:text-[#6B705C] transition-colors">
              {t("skip")}
            </button>
          )}
          {isRedoing && (
            <button type="button" onClick={onComplete} className="font-body text-sm text-[#A5A58D] hover:text-[#6B705C] transition-colors">
              {t("cancel")}
            </button>
          )}
        </div>
        <div className="w-full h-1.5 bg-[#EBE9E4] rounded-full overflow-hidden">
          <div className="h-full bg-[#7B9E7F] rounded-full transition-all duration-500" style={{ width: `${(step / totalSteps) * 100}%` }} />
        </div>
      </div>

      {/* Content - scrollable */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {renderStep()}
        {/* Spacer for scroll room */}
        <div className="h-24" />
      </div>

      {/* Navigation buttons - fixed at bottom, ABOVE everything */}
      <div className="shrink-0 px-6 pt-3 bg-[#FAFAF8] border-t border-[#EBE9E4]/50" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}>
        {error && (
          <div data-testid="onboarding-error" className="text-[#BC4749] text-sm font-body bg-[#BC4749]/10 rounded-xl px-4 py-3 mb-3">
            {error}
          </div>
        )}
        <div className="flex gap-3">
          {step > 1 && (
            <button type="button" data-testid="onboarding-back" onClick={goBack}
              className="flex items-center justify-center w-14 h-14 rounded-full bg-[#EBE9E4] text-[#3D4035] hover:bg-[#E0DDD7] transition-colors">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
          )}
          <button type="button" data-testid="onboarding-next" onClick={goNext} disabled={loading}
            className="flex-1 bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold text-lg hover:bg-[#6B8E6F] transition-all active:scale-[0.98] shadow-lg shadow-[#7B9E7F]/20 flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : step < totalSteps ? (
              <>{t("next")} <ArrowRight className="w-5 h-5" strokeWidth={1.5} /></>
            ) : (
              t("finish")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
